// Shared vocabulary for the Notices & Circulars module.
//
// A notice and a circular are the same record separated by `notice_type` — a
// circular is the formal, numbered communication a society issues (AGM calls,
// rule changes) while a notice is the everyday announcement. Keeping one entity
// means one list, one form and one detail view; the screens just preset the
// type filter.
//
// These maps mirror the enums the backend returns from /api/notices/enums. The
// FE keeps its own copy so a page still renders sensibly before that request
// resolves, and so Tailwind can see every class name literally (dynamic class
// strings are stripped by the JIT compiler).

export const NOTICE_TYPES = ['NOTICE', 'CIRCULAR'];

export const NOTICE_TYPE_STYLE = {
    NOTICE: 'tw-bg-sky-100 tw-text-sky-700',
    CIRCULAR: 'tw-bg-violet-100 tw-text-violet-700',
};

export const NOTICE_TYPE_ICON = {
    NOTICE: 'mdi:bullhorn-outline',
    CIRCULAR: 'mdi:file-document-outline',
};

export const STATUS_STYLE = {
    DRAFT: 'tw-bg-slate-100 tw-text-slate-600',
    SCHEDULED: 'tw-bg-indigo-100 tw-text-indigo-700',
    PUBLISHED: 'tw-bg-emerald-100 tw-text-emerald-700',
    ARCHIVED: 'tw-bg-gray-200 tw-text-gray-600',
    EXPIRED: 'tw-bg-amber-100 tw-text-amber-700',
};

export const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    NORMAL: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-700',
};

export const CATEGORY_STYLE = {
    GENERAL: 'tw-bg-slate-100 tw-text-slate-700',
    MAINTENANCE: 'tw-bg-cyan-100 tw-text-cyan-700',
    EVENT: 'tw-bg-fuchsia-100 tw-text-fuchsia-700',
    FINANCIAL: 'tw-bg-emerald-100 tw-text-emerald-700',
    SECURITY: 'tw-bg-rose-100 tw-text-rose-700',
    AGM: 'tw-bg-violet-100 tw-text-violet-700',
    EMERGENCY: 'tw-bg-red-100 tw-text-red-700',
};

export const CATEGORY_ICON = {
    GENERAL: 'mdi:information-outline',
    MAINTENANCE: 'mdi:wrench-outline',
    EVENT: 'mdi:calendar-star',
    FINANCIAL: 'mdi:currency-inr',
    SECURITY: 'mdi:shield-alert-outline',
    AGM: 'mdi:gavel',
    EMERGENCY: 'mdi:alert-octagon-outline',
};

// Fallbacks used until /api/notices/enums answers.
export const FALLBACK_ENUMS = {
    types: NOTICE_TYPES,
    statuses: ['DRAFT', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED', 'EXPIRED'],
    priorities: ['LOW', 'NORMAL', 'HIGH', 'URGENT'],
    categories: ['GENERAL', 'MAINTENANCE', 'EVENT', 'FINANCIAL', 'SECURITY', 'AGM', 'EMERGENCY'],
    audiences: ['ALL', 'OWNERS', 'TENANTS', 'COMMITTEE', 'STAFF', 'CUSTOM', 'FLATS'],
};

export const AUDIENCE_LABEL = {
    ALL: 'Everyone',
    OWNERS: 'Members (owners)',
    TENANTS: 'Tenants',
    COMMITTEE: 'Committee members',
    STAFF: 'Employees',
    FLATS: 'Specific flats…',
    CUSTOM: 'Specific members…',
};

// Audiences that need an extra selection before the notice can be saved.
export const AUDIENCE_NEEDS_PICKER = {
    FLATS: 'flat_ids',
    CUSTOM: 'recipient_user_ids',
};

export const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

export const fmtDateTime = (d) =>
    d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// A notice is live when it's published, its publish_at has passed and it hasn't
// expired. The backend flips status to EXPIRED on read, but a row already in
// the client's list can go stale between refreshes, so the UI recomputes it.
export const isExpired = (n) =>
    !!n?.expires_at && new Date(n.expires_at) < new Date();

export const isLive = (n) =>
    n?.status === 'PUBLISHED' && !isExpired(n);

// Effective status for display: promotes a stale PUBLISHED row to EXPIRED.
export const effectiveStatus = (n) =>
    (n?.status === 'PUBLISHED' && isExpired(n)) ? 'EXPIRED' : (n?.status || 'DRAFT');

// Strip tags for list previews — the body is stored as HTML from the editor.
export const toPlainText = (html) =>
    (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

export const excerpt = (html, len = 140) => {
    const text = toPlainText(html);
    return text.length > len ? `${text.slice(0, len)}…` : text;
};

export const authorName = (u) => {
    if (!u) return '—';
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return full || u.email || `User #${u.user_id}`;
};
